
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Lock, Loader2, AlertCircle, Mail, Check } from 'lucide-react';
import { toast } from 'sonner';

const ResetPasswordConfirm = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(5);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Verificar o código de autenticação na URL
  useEffect(() => {
    const checkAuthRecovery = async () => {
      // Log para debug
      console.log('Verificando link de recuperação de senha');
      console.log('URL completa:', window.location.href);
      
      // Verificamos primeiro se temos um confirmation_url para redirecionar
      const confirmUrl = searchParams.get('confirmation_url');
      
      if (confirmUrl) {
        console.log('Encontrado confirmation_url no parâmetro:', confirmUrl);
        // Redirecionar para a URL de confirmação completa do Supabase
        window.location.href = confirmUrl;
        return;
      }
      
      // Se não temos confirmation_url, verificamos se temos um código de recuperação
      const code = searchParams.get('code');
      
      // Verificar se temos o hash do tipo recovery na URL (caso antigo)
      const hash = window.location.hash;
      
      if (code) {
        // Novo formato - com parâmetro code
        console.log('Código de recuperação encontrado:', code);
        setVerifying(false);
      } else if (hash && hash.includes('type=recovery')) {
        // Formato antigo - com hash contendo type=recovery
        console.log('Hash válido para recuperação de senha encontrado');
        setVerifying(false);
      } else {
        console.error('Erro: Nenhum código de recuperação ou hash válido encontrado na URL');
        console.log('Parâmetros recebidos:', Object.fromEntries(searchParams.entries()));
        setError('Link de recuperação inválido ou expirado. Solicite a recuperação de senha novamente.');
        setVerifying(false);
      }
    };
    
    checkAuthRecovery();
  }, [searchParams, navigate]);

  // Contador regressivo para redirecionar após o sucesso
  useEffect(() => {
    if (success && redirectCountdown > 0) {
      const timer = setTimeout(() => {
        setRedirectCountdown(prev => prev - 1);
      }, 1000);
      
      return () => clearTimeout(timer);
    } else if (success && redirectCountdown === 0) {
      navigate('/login');
    }
  }, [success, redirectCountdown, navigate]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    // Validação da senha
    if (!password) {
      newErrors.password = 'Senha é obrigatória';
    } else if (password.length < 8) {
      newErrors.password = 'A senha deve ter pelo menos 8 caracteres';
    }
    
    // Validação da confirmação de senha
    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'As senhas não correspondem';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      console.log('Tentando atualizar senha');
      const { error } = await supabase.auth.updateUser({
        password
      });
      
      if (error) {
        toast.error('Erro ao atualizar senha: ' + error.message);
        console.error('Erro completo:', error);
        setIsSubmitting(false);
        return;
      }
      
      // Mostrar mensagem de sucesso
      console.log('Senha atualizada com sucesso');
      toast.success('Senha atualizada com sucesso');
      setSuccess(true);
      
      // Fazer logout
      await supabase.auth.signOut();
      
    } catch (err) {
      console.error('Erro ao atualizar senha:', err);
      toast.error('Ocorreu um erro, tente novamente');
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    if (id === 'password') {
      setPassword(value);
      // Limpar erro ao digitar
      if (errors.password) {
        setErrors(prev => ({ ...prev, password: '' }));
      }
    } else if (id === 'confirmPassword') {
      setConfirmPassword(value);
      // Limpar erro ao digitar
      if (errors.confirmPassword) {
        setErrors(prev => ({ ...prev, confirmPassword: '' }));
      }
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <h1 className="text-2xl font-bold text-blue-dark mb-4">Verificando link de recuperação</h1>
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="h-12 w-12 text-blue-main animate-spin mb-4" />
            <p className="text-gray-600">Verificando seu link, por favor aguarde...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <h1 className="text-2xl font-bold text-blue-dark mb-4">Erro na recuperação de senha</h1>
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <p className="text-red-700">{error}</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/reset-password')}
              className="flex items-center justify-center gap-2"
            >
              <Mail className="h-4 w-4" />
              <span>Solicitar novo link</span>
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate('/login')}
            >
              Voltar para o login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <h1 className="text-2xl font-bold text-blue-dark mb-4">Senha Atualizada</h1>
          <div className="flex flex-col items-center justify-center py-6">
            <div className="bg-green-50 w-20 h-20 rounded-full flex items-center justify-center mb-4">
              <Check className="h-10 w-10 text-green-500" />
            </div>
            <p className="text-gray-700 mb-4">Sua senha foi atualizada com sucesso!</p>
            <p className="text-gray-500">
              Redirecionando para a página de login em {redirectCountdown} segundos...
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={() => navigate('/login')}
            className="mt-4"
          >
            Ir para o login agora
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] py-12 px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-blue-dark">Redefinir Senha</h1>
          <p className="mt-2 text-gray-600">
            Crie uma nova senha para sua conta
          </p>
        </div>

        <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Nova Senha
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="Mínimo 8 caracteres"
                  value={password}
                  onChange={handleInputChange}
                  required
                  className={`pl-10 ${errors.password ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                />
              </div>
              {errors.password && (
                <p className="text-red-500 text-xs mt-1 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {errors.password}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirmar Nova Senha
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirme sua nova senha"
                  value={confirmPassword}
                  onChange={handleInputChange}
                  required
                  className={`pl-10 ${errors.confirmPassword ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                />
              </div>
              {errors.confirmPassword && (
                <p className="text-red-500 text-xs mt-1 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Atualizando...
                </>
              ) : (
                'Atualizar Senha'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordConfirm;
